import { styled } from "goober";
import { syntaxHighlightStyles } from "../syntaxHighlightStyles";
import { variantStyles } from "../Text";

const tokenStyles = syntaxHighlightStyles
  .map(
    ({ name, color, fontStyle }) =>
      `.tok-${name} { color: ${color};${fontStyle ? ` font-style: ${fontStyle};` : ""} }`
  )
  .join("\n      ");

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: var(--text-primary);
  min-width: 0;

  * {
    margin: 0;
  }

  h1,
  h2,
  h3 {
    font-size: ${variantStyles["display-md"].fontSize};
    line-height: ${variantStyles["display-md"].lineHeight};
    font-weight: ${variantStyles["display-md"].fontWeight};
  }

  h4,
  h5,
  h6 {
    font-size: ${variantStyles["text-regular-md"].fontSize};
    line-height: ${variantStyles["text-regular-md"].lineHeight};
    font-weight: ${variantStyles["text-regular-md"].fontWeight};
  }

  hr {
    margin: 8px 0;
    height: 0.5px;
    width: 100%;
    border: 0;
    background-color: var(--border-alpha);
  }

  p {
    font-size: inherit;
    line-height: inherit;
    font-weight: ${variantStyles["text-regular"].fontWeight};
  }

  b,
  strong {
    font-weight: ${variantStyles["text-regular-md"].fontWeight};
  }

  del,
  strike {
    color: var(--text-secondary);
  }

  code {
    font-family: var(--font-mono);
    color: var(--orange-800);
    border-radius: 4px;
    padding: 2px 4px;
    font-size: 10px;
    margin: 0 2px;
    background-color: var(--alpha4);
    border: 0.5px solid var(--border-alpha);
  }

  pre {
    background: var(--alpha4);
    border: 0.5px solid var(--border-alpha);
    border-radius: 8px;
    padding: 16px;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    code {
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      color: var(--text-primary);
      font-family: var(--font-mono);

      ${tokenStyles}
    }
  }

  a {
    color: var(--text-secondary);
  }

  ul,
  ol {
    margin: 0;
    padding-left: 20px;
    list-style-position: outside;
    ::marker {
      color: var(--text-secondary);
    }
  }

  li {
    padding-left: 4px;
  }

  ul > li + li {
    margin-top: var(--bullet-spacing);
  }

  ul li:has(input[type="checkbox"]) {
    list-style: none;
    margin-left: -20px;
    display: flex;
    align-items: center;

    input[type="checkbox"] {
      appearance: none;
      margin-right: 8px;
      &::before {
        content: " ";
        width: 10px;
        height: 10px;
        display: flex;
        border: 1px solid var(--border-alpha);
        border-radius: 50%;
      }
    }

    input[type="checkbox"]:checked {
      &::before {
        background: var(--green-600);
        border: 0;
      }
    }
  }

  blockquote {
    border-left: 4px solid var(--border-alpha);
    padding-left: 12px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    th,
    td {
      border-bottom: 0.5px solid var(--border-alpha);
      border-right: 0.5px solid var(--border-alpha);
      padding: 12px;
      margin: 0;
      white-space: nowrap;
      text-align: left;
      font-size: inherit;
      line-height: inherit;
      font-weight: ${variantStyles["text-regular"].fontWeight};
    }
    th {
      background: var(--alpha2);
      font-weight: ${variantStyles["text-regular-md"].fontWeight};
    }
    th:last-child,
    td:last-child {
      border-right: 0;
    }
    thead:first-child tr:first-child {
      th:first-child {
        border-top-left-radius: 12px;
      }
      th:last-child {
        border-top-right-radius: 12px;
      }
    }
    tbody:first-child tr:first-child {
      td:first-child {
        border-top-left-radius: 12px;
      }
      td:last-child {
        border-top-right-radius: 12px;
      }
    }
    tbody tr:last-child {
      td {
        border-bottom: 0;
      }
      td:first-child {
        border-bottom-left-radius: 12px;
      }
      td:last-child {
        border-bottom-right-radius: 12px;
      }
    }
  }
`;

export function MarkdownStyles({
  children,
  color,
  lineHeight,
  bulletSpacing = "0px",
  variant = "text-regular",
}: {
  children: React.ReactNode;
  color?: string;
  lineHeight?: string;
  bulletSpacing?: string;
  variant?: "text-regular" | "text-small";
}) {
  return (
    <Container
      style={
        {
          color,
          lineHeight,
          fontSize: variantStyles[variant].fontSize,
          "--bullet-spacing": bulletSpacing,
        } as React.CSSProperties
      }
    >
      {children}
    </Container>
  );
}
